import React, { useRef, useState, useCallback, FormEvent, useMemo, ReactNode, useEffect } from 'react'

import {
    Alert,
    AppSvg,
    Button,
    CoinIcon,
    FormattedCurrency,
    FormattedHTMLMessage,
    Input,
    newIcons,
    NumericInput,
} from '@web/walter-ui-kit'
import { useIntl } from 'react-intl'
import { GetCard } from '@web/walter-lib/api'
import Big from '@web/walter-lib/big.js'
import cn from 'classnames'
import { useSearchParams } from 'react-router-dom'
import s from './s.module.css'
import { lang } from './lang'
import Select from './Select/Select'
import Balances from './Balances/Balances'
import { globalLang } from '~globalLang'
import { cardsApi, useCards } from '~api'
import { useWalletByCurrency } from '~hooks/useWalletByCurrency'
import { usePromise } from '~hooks/usePromise'
import AddFunds from '~pages/CardsPage/components/AddFunds'
import { CardsViews } from '~pages/CardsPage/types'
import { QueryParams } from '~constants/routes'

interface Props {
    onSuccess: (from_currency: string, from_amount: string, id: string) => void
    isLoading: boolean
    initCard?: GetCard
    setPageView: (newView: CardsViews) => void
}

interface Error {
    type: 'zero' | 'less'
    message: ReactNode
}

const getLessError = (isBalanceLessThanAmountUser: boolean, currentCard?: GetCard) => (
    <div className={s.less}>
        <FormattedHTMLMessage {...(isBalanceLessThanAmountUser ? lang.lessFunds : lang.lessFundsFee)} />
        <AddFunds currency={currentCard?.currency} />
    </div>
)

// eslint-disable-next-line max-lines-per-function,complexity
function General({ onSuccess, isLoading, initCard, setPageView }: Props) {
    const intl = useIntl()
    const commissionRef = useRef<ReturnType<typeof setTimeout>>()

    const [searchParams, setSearchParams] = useSearchParams()
    const initAmount = searchParams.get(QueryParams.amount) || ''

    const { data: cards } = useCards()
    const activeCards = useMemo(
        () =>
            cards?.cards?.reduce((acc, card) => {
                if (card?.status === 'ACTIVE') {
                    acc.push(card)
                }

                return acc
            }, [] as GetCard[]) || [],
        [cards]
    )

    const initialCardId = useMemo(() => {
        if (initCard?.status === 'ACTIVE') {
            return initCard?.id
        }

        if (activeCards?.length) {
            return activeCards?.[0]?.id
        }

        return null
    }, [activeCards, initCard?.id, initCard?.status])

    const [cardId, setCardId] = useState<string | null>(initialCardId || null)
    const [amount, setAmount] = useState<string>('')
    const [isThrottling, setIsThrottling] = useState(false)
    const [error, setError] = useState<Error | undefined>()

    const supportLink = intl.formatMessage(globalLang.contactSupportLink)

    const currentCard = useMemo(() => cards?.cards?.find(card => card?.id === cardId), [cardId, cards?.cards])

    const { data: balance } = useWalletByCurrency(currentCard?.currency)

    const {
        send: getCommissions,
        isLoading: isComsLoading,
        data: comissions,
    } = usePromise(
        async ({ id, from_amount, from_currency }: { id: string; from_amount: string; from_currency: string }) => {
            return await cardsApi.getTopupCommissions({ id, from_amount, from_currency })
        }
    )

    const isCommissionsLoading = isComsLoading || isThrottling

    const isBalanceLessThanAmountToPay = Big(balance || 0).lt(Big(comissions?.calculated?.amount_to_pay || 0))
    const isBalanceLessThanAmountUser = Big(balance || 0).lt(Big(amount || 0))

    const isLessError = isBalanceLessThanAmountToPay || isBalanceLessThanAmountUser

    const onSuccessCb = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()

            if (!Number(amount)) {
                setError({ type: 'zero', message: <FormattedHTMLMessage {...lang.zero} /> })
            } else if (isLessError) {
                setError({
                    type: 'less',
                    message: getLessError(isBalanceLessThanAmountUser, currentCard),
                })
            } else {
                onSuccess(currentCard?.currency || '', amount, currentCard?.id || '')
            }
        },
        [amount, currentCard, isBalanceLessThanAmountUser, isLessError, onSuccess]
    )

    const throttleGetCommissions = useCallback(
        (id?: string, from_amount?: string, from_currency?: string, timeout = 0) => {
            if (id && from_amount && from_currency) {
                setIsThrottling(true)
                clearTimeout(commissionRef.current)
                commissionRef.current = setTimeout(() => {
                    setIsThrottling(false)
                    getCommissions({ id, from_amount, from_currency })
                }, timeout)
            }
        },
        [getCommissions]
    )

    const setAmountCb = useCallback(
        (amount: string) => {
            setAmount(amount)
            setError(undefined)
            throttleGetCommissions(currentCard?.id || '', amount, currentCard?.currency, 600)
        },
        [currentCard?.currency, currentCard?.id, throttleGetCommissions]
    )

    const setCardIdCb = useCallback(
        (newId: string | null) => {
            if (newId === 'newCard') {
                return setPageView(CardsViews.NewCards)
            }
            setCardId(newId)
            setError(undefined)
            throttleGetCommissions(newId || '', amount, currentCard?.currency)
        },
        [amount, currentCard?.currency, setPageView, throttleGetCommissions]
    )

    useEffect(() => {
        if (isCommissionsLoading) {
            return
        }

        if (error?.type === 'less' && !isLessError) {
            setError(undefined)
            return
        }

        if (isLessError) {
            setError({
                type: 'less',
                message: getLessError(isBalanceLessThanAmountUser, currentCard),
            })
        }
    }, [balance, currentCard, error?.type, isBalanceLessThanAmountUser, isCommissionsLoading, isLessError])

    useEffect(() => {
        if (!initAmount || !!amount) {
            return
        }

        setAmountCb(initAmount)

        setSearchParams(
            prev => {
                prev.delete(QueryParams.amount)
                return prev
            },
            { replace: true }
        )
    }, [amount, initAmount, setAmountCb, setSearchParams])

    return (
        <form onSubmit={onSuccessCb} className={s.wrap}>
            <div className={s.select}>
                <Input
                    label={intl.formatMessage(lang.fromWallet)}
                    value={currentCard?.currency || ''}
                    dataPointer="currency_input"
                    prefix={<CoinIcon code={currentCard?.currency || ''} size="xs" />}
                    suffix={
                        <div className={s.inputSuffix} data-pointer="topup_wallet_balance">
                            <div className={cn(s.suffix, !isCommissionsLoading && isLessError && s.error)}>
                                <FormattedCurrency
                                    maxPrecision={2}
                                    value={balance}
                                    currency={currentCard?.currency || ''}
                                    dataPointer="balance"
                                />
                            </div>
                            <AppSvg
                                svgName={newIcons.ic24ChevronDown}
                                size={24}
                                className={s.arrowDown}
                                path="new-icons"
                            />
                        </div>
                    }
                    readOnly
                    className={s.input}
                />
                <div className={s.switcher}>
                    <AppSvg svgName={newIcons.ic24ArrowDown} path="new-icons" size={24} />
                </div>
                <Select onSelect={setCardIdCb} value={cardId} currentCard={currentCard} />
            </div>
            <NumericInput
                label={intl.formatMessage(lang.amount)}
                value={amount}
                onChange={setAmountCb}
                precision={2}
                autoFocus
                suffix={currentCard?.currency}
                dataPointer="amount_input"
                isInvalid={!!error?.type}
                description={error?.message}
            />
            <Balances
                isLoading={isCommissionsLoading}
                card={currentCard}
                data={comissions}
                isAmountError={isLessError}
            />
            <div className={s.alert}>
                <Alert
                    dataPointer="topup_alert"
                    text={<FormattedHTMLMessage {...lang.alert} values={{ supportLink }} />}
                    type="warning"
                />
            </div>
            <Button
                isDisabled={isCommissionsLoading}
                dataPointer="topup_submit"
                size="l"
                mode="primary"
                htmlButtonType="submit"
                fullWidth
                isLoading={isLoading}
            >
                <FormattedHTMLMessage {...lang.topUp} />
            </Button>
        </form>
    )
}

export default General
